import * as React from 'react';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router';
import { useLanguage } from '../../contexts/LanguageContext';
import { getNotifications, markAsRead, markAllAsRead, type Notification } from '../data/notifications';
import { fcmService } from '../services/fcmService';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ar';
import 'dayjs/locale/en';

// Extend dayjs with relativeTime plugin
dayjs.extend(relativeTime);

export default function NotificationsMenu() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, language, direction } = useLanguage();
  const isRtl = direction === 'rtl';
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [notifications, setNotifications] = React.useState<Notification[]>([]);
  const [page, setPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);
  const listRef = React.useRef<HTMLDivElement>(null);

  const unreadCount = React.useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const loadNotifications = React.useCallback(async (pageNum: number) => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const response = await getNotifications(pageNum);
      
      if (pageNum === 1) {
        setNotifications(response.data);
      } else {
        setNotifications((prev) => [...prev, ...response.data]);
      }
      
      setHasMore(response.current_page < response.last_page);
      setPage(pageNum);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setIsLoading(false);
      setIsInitialLoad(false);
    }
  }, [isLoading]);

  // Load initial notifications
  React.useEffect(() => {
    loadNotifications(1);
  }, []);

  // Listen to Firebase Cloud Messaging for new notifications
  React.useEffect(() => {
    const unsubscribe = fcmService.onMessage((payload) => {
      console.log('Received FCM message in NotificationsMenu:', payload);
      // Refresh notifications when a new message is received
      loadNotifications(1);
    });

    return unsubscribe;
  }, [loadNotifications]);

  const handleScroll = React.useCallback(() => {
    if (!listRef.current || isLoading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    
    // Load more when scrolled to bottom (with 50px threshold)
    if (scrollHeight - scrollTop - clientHeight < 50) {
      loadNotifications(page + 1);
    }
  }, [isLoading, hasMore, page, loadNotifications]);

  const handleNotificationClick = React.useCallback(async (notification: Notification) => {
    // Handle navigation based on notification_type
    if (notification.notification_type) {
      const notificationType = notification.notification_type.toLowerCase();

      // Close the popover
      handleClose();

      // Navigate based on notification type
      if (notificationType.includes('leave_request') && notification.related_leave_request?.id) {
        // Navigate to leave requests page with id query parameter
        navigate(`/leave-requests?id=${notification.related_leave_request.id}`);
      } else if (notificationType.includes('installment_due') && notification.related_installment?.order?.id) {
        // Navigate to order view page
        navigate(`/orders/${notification.related_installment.order.id}`);
      } else if (notificationType.includes('gm_message')) {
        // Navigate to broadcast messages page
        navigate('/broadcasts');
      }
    }

    // Mark as read if unread (after navigation)
    if (!notification.read) {
      try {
        await markAsRead([notification.id]);

        // Update local state
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id ? { ...n, read: true } : n
          )
        );
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }
  }, [navigate]);

  const handleMarkAllAsRead = React.useCallback(async () => {
    if (unreadCount === 0) return;
    try {
      await markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, [unreadCount]);

  const getTimeAgo = (dateString: string) => {
    try {
      // Set locale based on current language
      const locale = language === 'ar' ? 'ar' : 'en';
      return dayjs(dateString).locale(locale).fromNow();
    } catch {
      return dateString;
    }
  };

  return (
    <React.Fragment>
      <Tooltip title={t('notifications.title')} enterDelay={1000}>
        <div>
          <IconButton
            size="medium"
            onClick={handleClick}
            sx={(theme) => ({
              color: theme.palette.text.primary,
              backgroundColor: 'rgba(0, 0, 0, 0.03)',
              borderRadius: '12px',
              border: '1px solid transparent',
              transition: 'all 0.3s ease',
              marginLeft: isRtl ? '8px' : 0,
              ...theme.applyStyles('dark', {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
              }),
              '&:hover': {
                backgroundColor: '#028bcb',
                color: '#fff',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(2, 139, 203, 0.3)',
                border: '1px solid rgba(2, 139, 203, 0.5)',
                ...theme.applyStyles('dark', {
                  boxShadow: '0 6px 20px rgba(2, 139, 203, 0.4), 0 0 0 1px rgba(2, 139, 203, 0.5)',
                }),
              },
            })}
          >
            <Badge
              badgeContent={unreadCount > 5 ? '+5' : unreadCount}
              color="error"
              max={99}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  minWidth: '18px',
                  height: '18px',
                  padding: '0 4px',
                },
              }}
            >
              {unreadCount > 0 ? <NotificationsActiveIcon /> : <NotificationsIcon />}
            </Badge>
          </IconButton>
        </div>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isRtl ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: !isRtl ? 'left' : 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1.5,
              width: 420,
              maxWidth: '90vw',
              maxHeight: 650,
              borderRadius: '20px',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 20px 60px rgba(0, 0, 0, 0.8), 0 0 1px rgba(255, 255, 255, 0.1)'
                : '0 20px 60px rgba(0, 0, 0, 0.15), 0 0 1px rgba(0, 0, 0, 0.05)',
              overflow: 'hidden',
              border: `1px solid ${theme.palette.divider}`,
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(30, 30, 30, 0.98) 0%, rgba(20, 20, 20, 0.98) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 252, 0.98) 100%)',
              backdropFilter: 'blur(20px)',
            },
          },
        }}
      >
        <Box
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(40, 40, 40, 0.6) 0%, rgba(30, 30, 30, 0.6) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              textAlign: isRtl ? 'right' : 'left',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #fff 0%, #e0e0e0 100%)'
                : 'linear-gradient(135deg, #1a1a1a 0%, #4a4a4a 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {t('notifications.title')}
          </Typography>
          {unreadCount > 0 && (
            <Typography
              variant="caption"
              onClick={handleMarkAllAsRead}
              sx={{
                display: 'block',
                textAlign: isRtl ? 'right' : 'left',
                color: '#028bcb',
                fontWeight: 600,
                cursor: 'pointer',
                mt: 0.5,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {t('notifications.markAllAsRead')}
            </Typography>
          )}

        </Box>

        <Box
          ref={listRef}
          onScroll={handleScroll}
          sx={{
            maxHeight: 550,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '10px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
              margin: '8px 0',
            },
            '&::-webkit-scrollbar-thumb': {
              background: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.2)'
                : 'rgba(0, 0, 0, 0.15)',
              borderRadius: '10px',
              border: '2px solid transparent',
              backgroundClip: 'padding-box',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.3)'
                : 'rgba(0, 0, 0, 0.25)',
              backgroundClip: 'padding-box',
            },
          }}
        >
          {isInitialLoad ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
              <CircularProgress
                size={40}
                thickness={4}
                sx={{
                  color: '#028bcb',
                }}
              />
            </Box>
          ) : notifications.length === 0 ? (
            <Box
              sx={{
                p: 6,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(2, 139, 203, 0.15) 0%, rgba(0, 102, 204, 0.1) 100%)'
                    : 'linear-gradient(135deg, rgba(2, 139, 203, 0.1) 0%, rgba(0, 102, 204, 0.05) 100%)',
                  border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(2, 139, 203, 0.3)' : 'rgba(2, 139, 203, 0.2)'}`,
                }}
              >
                <NotificationsIcon
                  sx={{
                    fontSize: 40,
                    color: '#028bcb',
                    opacity: 0.7,
                  }}
                />
              </Box>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                }}
              >
                {t('notifications.noNotifications')}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {notifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  {index > 0 && (
                    <Divider
                      sx={{
                        borderColor: theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.05)'
                          : 'rgba(0, 0, 0, 0.06)',
                      }}
                    />
                  )}
                  <ListItem
                    disablePadding
                    sx={{
                      position: 'relative',
                      '&::before': !notification.read ? {
                        content: '""',
                        position: 'absolute',
                        [isRtl ? 'right' : 'left']: 0,
                        top: 0,
                        bottom: 0,
                        width: '4px',
                        background: 'linear-gradient(180deg, #028bcb 0%, #0066cc 100%)',
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 0 10px rgba(2, 139, 203, 0.5)'
                          : '0 0 8px rgba(2, 139, 203, 0.3)',
                      } : {},
                    }}
                  >
                    <ListItemButton
                      onClick={() => handleNotificationClick(notification)}
                      sx={{
                        py: 2,
                        px: 3,
                        [isRtl ? 'pr' : 'pl']: !notification.read ? 4 : 3,
                        position: 'relative',
                        background: !notification.read
                          ? theme.palette.mode === 'dark'
                            ? 'linear-gradient(90deg, rgba(2, 139, 203, 0.15) 0%, rgba(2, 139, 203, 0.05) 100%)'
                            : 'linear-gradient(90deg, rgba(2, 139, 203, 0.08) 0%, rgba(2, 139, 203, 0.02) 100%)'
                          : 'transparent',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          background: !notification.read
                            ? theme.palette.mode === 'dark'
                              ? 'linear-gradient(90deg, rgba(2, 139, 203, 0.25) 0%, rgba(2, 139, 203, 0.1) 100%)'
                              : 'linear-gradient(90deg, rgba(2, 139, 203, 0.12) 0%, rgba(2, 139, 203, 0.04) 100%)'
                            : theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.05)'
                              : 'rgba(0, 0, 0, 0.03)',
                          transform: 'translateX(2px)',
                        },
                      }}
                    >
                      <ListItemText
                        sx={{
                          textAlign: isRtl ? 'right' : 'left',
                          direction: isRtl ? 'rtl' : 'ltr',
                        }}
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            {!notification.read && (
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  background: 'linear-gradient(135deg, #028bcb 0%, #0066cc 100%)',
                                  boxShadow: theme.palette.mode === 'dark'
                                    ? '0 0 8px rgba(2, 139, 203, 0.6)'
                                    : '0 0 6px rgba(2, 139, 203, 0.4)',
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            <Typography
                              variant="body2"
                              fontWeight={!notification.read ? 700 : 500}
                              sx={{
                                flex: 1,
                                color: !notification.read
                                  ? theme.palette.mode === 'dark'
                                    ? '#fff'
                                    : '#1a1a1a'
                                  : theme.palette.text.primary,
                                letterSpacing: !notification.read ? '0.01em' : 'normal',
                              }}
                            >
                              {notification.title}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography
                              variant="body2"
                              sx={{
                                mb: 0.5,
                                color: !notification.read
                                  ? theme.palette.text.primary
                                  : theme.palette.text.secondary,
                                opacity: !notification.read ? 0.95 : 0.8,
                                lineHeight: 1.5,
                              }}
                            >
                              {isRtl ? notification.arabic_message : notification.english_message}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#028bcb',
                                fontWeight: !notification.read ? 600 : 500,
                                fontSize: '0.7rem',
                                letterSpacing: '0.02em',
                              }}
                            >
                              {getTimeAgo(notification.created_at)}
                            </Typography>
                          </>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                </React.Fragment>
              ))}
              {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress
                    size={28}
                    thickness={4}
                    sx={{
                      color: '#028bcb',
                    }}
                  />
                </Box>
              )}
            </List>
          )}
        </Box>
      </Popover>
    </React.Fragment>
  );
}

